import { useState } from 'react';
import { closeAccount } from '../../lib/api';
import { useApiMutation } from '../../hooks/useApi';
import type { Customer } from '../../types';
import Modal from '../Modal';

interface CloseAccountProps {
  customer: Customer;
  onClose: () => void;
  onSuccess: () => void;
}

const closeReasons = [
  'Customer request',
  'Fraud detected',
  'Terms of service violation',
  'Regulatory requirement',
  'Duplicate account',
  'Deceased',
  'Other',
];

export default function CloseAccount({ customer, onClose, onSuccess }: CloseAccountProps) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');

  const { loading, error, execute } = useApiMutation(closeAccount, {
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await execute({
      customerId: customer.id,
      customerEmail: customer.email,
      reason,
      notes,
    });
  };

  const isConfirmed = confirmEmail === customer.email;

  return (
    <Modal title="Close Account" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800 font-medium">Warning: This action cannot be undone.</p>
            <p className="text-sm text-red-700 mt-1">
              You are about to permanently close the account for{' '}
              <strong>
                {customer.first_name} {customer.last_name}
              </strong>
              .
            </p>
          </div>

          <div>
            <label className="label">Reason for Closure</label>
            <select
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason...</option>
              {closeReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Additional Notes (Optional)</label>
            <textarea
              className="input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context..."
            />
          </div>

          <div>
            <label className="label">
              Type the customer email to confirm: <code className="text-xs">{customer.email}</code>
            </label>
            <input
              type="text"
              className="input"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder="Enter customer email..."
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            className="btn-danger"
            disabled={loading || !reason || !isConfirmed}
          >
            {loading ? 'Closing...' : 'Close Account'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
